// Selection-region publisher + visual sync. Picking primitives now live in
// `@scene/picking`; callers should import those directly. This module owns
// the side effects that bridge picking → store.

import { store } from '@store/instance';
import { updateProjectionLines, applyMarkerTheme } from '@scene/picking';
import type { Viewer, ParamsLike } from './types';
import type { RegionContext } from './regionContext';
import * as THREE from 'three';

/**
 * Compute the physical position + field sample for `point` (Three.js coords)
 * and publish to `store.selection.{pos, sample}`. UI subscribers render.
 */
export function updateSelectedRegion(ctx: RegionContext | null | undefined, point: THREE.Vector3 | null | undefined): void {
  if (!ctx || !point) return;
  const { threeJSToPhysical: t2p, getFieldValue: gfv } = ctx;
  const posPhys = t2p(point.x, point.y, point.z);
  let value = 0;
  try { value = gfv(posPhys.x, posPhys.y, posPhys.z); } catch { value = 0; }
  store.set('selection', { pos: { x: posPhys.x, y: posPhys.y, z: posPhys.z }, sample: { value } });
}

/**
 * Sync every selection-visual (wireframe, center dot, hover ring, projection
 * lines) to the invisible cube's current Three.js position. The center dot's
 * color is theme-driven (set at init / on theme apply), not field-driven.
 */
export function updateSelectionVisuals(viewer: Viewer, params: ParamsLike): void {
  void params;
  const sel = viewer.objects.selection;
  const inv = sel.invisibleMesh;
  if (!inv) return;

  if (sel.wireframe) sel.wireframe.position.copy(inv.position);
  if (sel.centerDot) sel.centerDot.position.copy(inv.position);
  try { updateProjectionLines(); } catch { /* swallow */ }
  if (sel.cube) sel.cube.position.copy(inv.position);
}

/**
 * Recolor the selection sphere + helper lines from the active theme, and let
 * the marker module re-apply outline opacity / tunables. Called on theme apply
 * so the visuals follow the active theme block at `theme.scene.selection` (plus
 * the global accent fallback).
 *
 * Note: toggling the `glow` flag at runtime is not handled here — the outline
 * sprite is created/skipped once in picking/setup.ts. A theme change that flips
 * `glow` only takes effect on the next page load.
 */
export function applyThemeToSelectionVisuals(viewer: Viewer): void {
  const sel = viewer.objects.selection;
  const theme = viewer.theme?.current;
  if (!theme) return;

  const selTheme = theme.scene?.selection;
  const sphereColor = selTheme?.sphereColor || theme.ui?.accent;
  if (sphereColor && sel.centerDot) {
    const mat = Array.isArray(sel.centerDot.material) ? sel.centerDot.material[0] : sel.centerDot.material;
    if (mat && 'color' in mat && (mat as THREE.MeshBasicMaterial).color instanceof THREE.Color) {
      (mat as THREE.MeshBasicMaterial).color.set(sphereColor);
    }
  }
  // Re-snapshots the core base color (set just above) + outline opacity.
  applyMarkerTheme(selTheme);

  const gridColor = theme.scene?.grid?.color;
  const projOverride = (selTheme && typeof selTheme.projectionOpacity === 'number') ? selTheme.projectionOpacity : null;
  const dashSize = (selTheme && typeof selTheme.projectionDashSize === 'number') ? selTheme.projectionDashSize : null;
  const gapSize = (selTheme && typeof selTheme.projectionGapSize === 'number') ? selTheme.projectionGapSize : null;
  if (sel.projectionLines) {
    sel.projectionLines.children.forEach((child) => {
      const line = child as THREE.Line;
      const m = Array.isArray(line.material) ? line.material[0] : line.material;
      if (!m) return;
      const lm = m as THREE.LineBasicMaterial;
      if (gridColor && lm.color instanceof THREE.Color) lm.color.set(gridColor);
      if (projOverride !== null) { lm.opacity = projOverride; lm.transparent = projOverride < 1.0; }
      // Dash params + line distances apply only to the dashed drop line.
      if (lm instanceof THREE.LineDashedMaterial) {
        if (dashSize !== null) lm.dashSize = dashSize;
        if (gapSize !== null) lm.gapSize = gapSize;
        try { line.computeLineDistances(); } catch { /* swallow */ }
      }
      lm.needsUpdate = true;
    });
  }
}
